<template>
  <div class="container-fluid">
    <!-- page header -->
    <b-row class="mt-5 mb-3">
      <b-col sm="12" md="6">
        <h4>{{ title }}</h4>
        <b-btn
          v-if="isArchived"
          :to="{ name: 'admin.notifications' }"
          pill
          variant="info"
          class="text-white add-user"
        >
          <span>Retour à vos notifications reçues</span>
        </b-btn>
      </b-col>
    </b-row>

    <!-- filter and search -->
    <div class="bg-light py-4 container-fluid">
      <b-form inline class="justify-content-end mb-2">
        <div class="mb-2">
          <label class="mr-3 d-inline" for="inline-form-input-name"
            >Type de notification</label
          >
          <b-form-select
            v-model="typeNotificationSelected"
            :options="typeNotificationOptions"
            class="mr-3"
            id="select-horizontal"
          >
            <template v-slot:first>
              <b-form-select-option :value="null">
                Choisir le type de notification
              </b-form-select-option>
            </template>
          </b-form-select>
          <label class="sr-only" for="inline-form-input-name">search</label>
          <b-input
            id="inline-form-input-name"
            class="mb-2 mr-sm-2 mb-sm-0"
            placeholder="Recherche"
            v-model="name"
            @change="onChange({ search: $event })"
          ></b-input>
          <b-btn :disabled="!name" pill variant="primary">Rechercher</b-btn>
        </div>
      </b-form>
      <b-row align-h="end">
        <b-col cols="12" class="text-right"
          ><b-button
            class="text-info"
            v-if="!isArchived"
            variant="link"
            :to="{
              name: 'admin.archived.notifications'
            }"
            >Voir les notifications archivées</b-button
          ></b-col
        >
      </b-row>
    </div>
    <!-- table -->
    <b-table
      bordered
      outlined
      show-empty
      class="mb-5"
      stacked="md"
      :items="notifications.data.data"
      :fields="fields"
      :busy="isBusy"
      empty-text="Il n'y a pas de notifications correspondant à vos critères de recherche"
    >
      <template v-slot:cell(user_email)="row">
        <router-link
          :to="{
            name: 'admin.student.profile',
            params: { id: row.item.user[0].id }
          }"
        >
          {{ row.value }}</router-link
        >
      </template>
      <template v-slot:cell(teacher)="row">
        <router-link
          :to="{
            name: 'admin.usersProfile',
            params: { id: row.item.teacher[0].id }
          }"
        >
          {{ row.value[0].name + " " + row.value[0].last_name }}</router-link
        >
      </template>
      <template v-slot:cell(actions)="row">
        <b-dropdown
          size="sm"
          variant="none"
          toggle-class="text-decoration-none"
          no-caret
          right
          pill
        >
          <template slot="button-content">
            <img
              src="@/assets/img/u3489.png"
              class="d-inline-block align-center"
            />
          </template>
          <b-dropdown-item
            :disabled="isArchived"
            @click="
              onArchivenotification({
                id: row.item.id,
                status: 0
              })
            "
          >
            Archiver la notification
          </b-dropdown-item>
          <b-dropdown-item
            @click="
              oninactiveAccount({
                user_id: row.item.user[0].id,
                status: 0
              })
            "
            >Supprimer le compte du participant</b-dropdown-item
          >
        </b-dropdown>
      </template>
    </b-table>
    <!-- User Interface controls -->
    <b-row>
      <b-col cols="4" class="my-1">
        <b-pagination
          pills
          v-model="table.currentPage"
          :total-rows="table.totalRows"
          :per-page="table.perPage"
          class="my-0"
          @change="pageChange"
        ></b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/**
 * @file       Notifications component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import { mapState } from "vuex";
import store from "@/store";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  /**
   * Before route change.
   * 
   * @param  {string}   to     Destination
   * @param  {string}   from   (Unused)
   * @param  {Object}   next   The action
   */
  beforeRouteEnter(to, from, next) {
    if (to.name == "admin.archived.notifications") {
      store.commit("admin/setNotificationFilters", {
        reset: true
      });
    } else {
      store.commit("admin/setNotificationFilters", {
        reset: true,
        fields: { status: 1 }
      });
    }
    store.dispatch("admin/getNotifications").then(() => next());
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      name: null,
      fields: [
        {
          key: "notification_date",
          label: "Date",
          sortable: true
        },
        {
          key: "user_email",
          label: "Adresse courriel du participant",
          sortable: true,
          formatter: (value, key, item) => {
            return item.user[0].email;
          }
        },
        {
          key: "type",
          label: "Type de notification",
          sortable: true,
          formatter: (value, key, item) => {
            return item.type == 1 ? "Signalement" : "Participant suspendu";
          }
        },
        {
          key: "tagged_comment",
          label: "Publication signalée",
          sortable: true
        },
        {
          key: "teacher",
          label: "Nom de l'enseignant",
          sortable: true
        },
        {
          key: "actions",
          label: "",
          sortable: false,
          class: "text-center"
        }
      ],
      typeNotificationOptions: [
        {
          text: "Signalement",
          value: 1
        },
        {
          text: "Participant suspendu",
          value: 2
        }
      ]
    };
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      notifications: state => state.admin.notifications
    }),

    /**
     * Pager table.
     *
     * @return     {Object}
     */
    table() {
      return {
        totalRows: this.notifications.data.total,
        currentPage: this.notifications.data.current_page,
        perPage: this.notifications.data.per_page
      };
    },

    /**
     * Check if the status of notifications is busy.
     *
     * @return     {bool}  Busy or not.
     */
    isBusy() {
      return this.notifications.status === "fetching";
    },

    /**
     * Type notification selected.
     */
    typeNotificationSelected: {
      get() {
        return this.$store.state.admin.notifications.filters.type;
      },
      set(value) {
        this.$store.commit("admin/setNotificationFilters", {
          fields: { type: value }
        });
        this.$store.dispatch("admin/getNotifications");
      }
    },

    /**
     * Check if the route is "archived".
     *
     * @return     {bool}  true or false.
     */
    isArchived() {
      return this.$route.name == "admin.archived.notifications";
    },

    /**
     * Get notification label.
     *
     * @return     {String}  The label.
     */
    title() {
      return this.isArchived
        ? "Notifications archivées"
        : "Les notifications reçues";
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Go to arborescence graph.
     */
    goArborescence() {
      this.$router.push({
        name: "deliberation.arborescenceGraph",
        params: { teacher: this.dlibr.teacher, url: this.dlibr.url }
      });
    },

    /**
     * Go to keyword graph.
     */
    goKeyword() {
      this.$router.push({
        name: "deliberation.keywordGraph",
        params: { teacher: this.dlibr.teacher, url: this.dlibr.url }
      });
    },

    /**
     * Go to stats graph.
     */
    goStats() {
      this.$router.push({
        name: "deliberation.statsGraph",
        params: { teacher: this.dlibr.teacher, url: this.dlibr.url }
      });
    },

    /**
     * Go to timeline.
     */
    goTimeline() {
      this.$router.push({
        name: "deliberation.byUrl",
        params: { teacher: this.dlibr.teacher, url: this.dlibr.url }
      });
    },

    /**
     * Change notification page.
     *
     * @param      {int}  page     The page number
     */
    pageChange(page) {
      this.$store.commit("admin/setNotificationFilters", { fields: { page } });
      this.$store.dispatch("admin/getNotifications");
    },

    /**
     * Change notification.
     *
     * @param      {Object}  select     The selected item
     */
    onChange(select) {
      this.$store.commit("admin/setNotificationFilters", {
        fields: { ...select }
      });
      this.$store.dispatch("admin/getNotifications");
    },

    /**
     * Archive notification.
     *
     * @param      {Object}  payload     The notification
     */
    onArchivenotification(payload) {
      store
        .dispatch("admin/putNotification", payload)
        .then(() => {
          this.$root.$bvToast.toast(
            `La notification a été archivé avec succès.`,
            {
              title: "Succès"
            }
          );
        })
        .catch(() => {
          this.$root.$bvToast.toast("La notification n'a pu être supprimé", {
            title: "Erreur",
            variant: "danger"
          });
        });
    },

    /**
     * Inactive account.
     *
     * @param      {Object}  payload     The user
     */
    oninactiveAccount(payload) {
      store
        .dispatch("admin/putUser", payload)
        .then(() => {
          this.$root.$bvToast.toast("Requête envoyé avec succès.", {
            title: "Succès"
          });
        })
        .catch(() => {
          this.$root.$bvToast.toast("La requête a échoué", {
            title: "Erreur",
            variant: "danger"
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped></style>
